
import { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { CartProvider } from './hooks/useCart';
import { FavoritesProvider } from './hooks/useFavorites';
import { AppRoutes } from './router/index';
import ScrollToTop from './components/common/ScrollToTop';

function App() {
  useEffect(() => {
    const STORAGE_KEY = 'vapiButtonPos';

    const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max);

    const applyPosition = (el: HTMLElement, x: number, y: number) => {
      el.style.position = 'fixed';
      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
      el.style.right = '';
      el.style.bottom = '';
      el.style.zIndex = '9999';
    };

    const attachDraggable = (el: HTMLElement) => {
      try {
        const startPos = (() => {
          try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) {
              const { x, y } = JSON.parse(raw);
              return { x: Number(x) || 0, y: Number(y) || 0 };
            }
          } catch {}
          const vw = window.innerWidth;
          const vh = window.innerHeight;
          const rect = el.getBoundingClientRect();
          return { x: vw - rect.width - 24, y: vh - rect.height - 24 };
        })();

        applyPosition(el, startPos.x, startPos.y);

        let dragging = false;
        let offsetX = 0;
        let offsetY = 0;

        // Smoothing via rAF throttling
        let rafId = 0;
        let nextX = startPos.x;
        let nextY = startPos.y;

        const flush = () => {
          applyPosition(el, nextX, nextY);
          rafId = 0;
        };

        const onPointerDown = (ev: PointerEvent) => {
          dragging = true;
          el.setPointerCapture(ev.pointerId);
          const rect = el.getBoundingClientRect();
          offsetX = ev.clientX - rect.left;
          offsetY = ev.clientY - rect.top;
          // Disable transition during drag
          el.style.transition = 'none';
          el.style.willChange = 'left, top';
        };

        const onPointerMove = (ev: PointerEvent) => {
          if (!dragging) return;
          const vw = window.innerWidth;
          const vh = window.innerHeight;
          const rect = el.getBoundingClientRect();
          nextX = clamp(ev.clientX - offsetX, 8, vw - rect.width - 8);
          nextY = clamp(ev.clientY - offsetY, 8, vh - rect.height - 8);
          if (!rafId) {
            rafId = requestAnimationFrame(flush);
          }
        };

        const onPointerUp = (ev: PointerEvent) => {
          if (!dragging) return;
          dragging = false;
          el.releasePointerCapture(ev.pointerId);
          const rect = el.getBoundingClientRect();
          const vw = window.innerWidth;
          const leftEdge = 8;
          const rightEdge = vw - rect.width - 8;
          const snappedX = Math.abs(rect.left - leftEdge) <= Math.abs(rightEdge - rect.left) ? leftEdge : rightEdge;
          // Snap with smooth transition
          el.style.transition = 'left 200ms ease, top 200ms ease';
          applyPosition(el, snappedX, rect.top);
          // Persist snapped position
          localStorage.setItem(STORAGE_KEY, JSON.stringify({ x: snappedX, y: rect.top }));
          // Clear transition after snap completes
          setTimeout(() => { el.style.transition = 'none'; }, 220);
        };

        el.style.touchAction = 'none';
        el.addEventListener('pointerdown', onPointerDown);
        window.addEventListener('pointermove', onPointerMove, { passive: true });
        window.addEventListener('pointerup', onPointerUp, { passive: true });

        const onResize = () => {
          try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return;
            const { x, y } = JSON.parse(raw);
            const rect = el.getBoundingClientRect();
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            applyPosition(el, clamp(x, 8, vw - rect.width - 8), clamp(y, 8, vh - rect.height - 8));
          } catch {}
        };
        window.addEventListener('resize', onResize);

        return () => {
          if (rafId) cancelAnimationFrame(rafId);
          window.removeEventListener('pointermove', onPointerMove as any);
          window.removeEventListener('pointerup', onPointerUp as any);
          window.removeEventListener('resize', onResize);
          el.removeEventListener('pointerdown', onPointerDown as any);
        };
      } catch {}
    };

    let cleanup: (() => void) | undefined;

    const tryAttach = () => {
      const el = document.querySelector('#vapi-widget-floating-button') as HTMLElement | null;
      if (el) cleanup = attachDraggable(el);
    };

    tryAttach();

    const mo = new MutationObserver(() => tryAttach());
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      if (cleanup) cleanup();
    };
  }, []);

  return (
    <BrowserRouter basename={__BASE_PATH__}>
      <CartProvider>
        <FavoritesProvider>
          <div className="min-h-screen bg-white">
            <ScrollToTop />
            <AppRoutes />
          </div>
        </FavoritesProvider>
      </CartProvider>
    </BrowserRouter>
  );
}

export default App;
